/**
 * BottomTabBar - Barra de navegação inferior fixa
 * 
 * Componente de navegação com 5 abas:
 * - Perfil (Profile)
 * - Desempenho (Performance)
 * - Questões (Concursos)
 * - Desafios (Challenges)
 * - Suporte (Support)
 * 
 * Otimizações:
 * - useMemo para cores (evita recriação)
 * - useCallback para handlers (evita recriação)
 * - Escala responsiva baseada na largura da tela
 * - Pressable com feedback de escala
 */

import React, { memo, useMemo, useCallback } from 'react';
import { View, Pressable, StyleSheet, Dimensions, Platform } from 'react-native';
import { Text } from '../ui/AppText';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSupportNotification } from '../../contexts/SupportContext';
import Svg, { Path } from 'react-native-svg';

// Dimensões da tela
const { width: SCREEN_WIDTH } = Dimensions.get('window');

// Sistema de escala responsiva (baseado em iPhone SE - 375px)
const BASE_SCREEN_WIDTH = 375;
const scale = (size) => (SCREEN_WIDTH / BASE_SCREEN_WIDTH) * size;

// Valores responsivos com limites min/max para consistência
const ICON_SIZE = Math.min(Math.max(Math.round(scale(20)), 18), 26);
const FONT_SIZE = Math.min(Math.max(Math.round(scale(10)), 9), 13);
const MIN_TAB_WIDTH = Math.min(Math.max(Math.round(scale(55)), 50), 75);
const PADDING_TOP = Math.min(Math.max(Math.round(scale(8)), 6), 12);
const MARGIN_TOP_LABEL = Math.min(Math.max(Math.round(scale(3)), 2), 5);

// Ícones SVG para as abas
const HomeIcon = ({ color, size = 24, filled = false }) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : "none"}>
        <Path
            d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill={filled ? color : "none"}
        />
        <Path
            d="M9 22V12h6v10"
            stroke={filled ? '#151821' : color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </Svg>
);

const ChartIcon = ({ color, size = 24 }) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path
            d="M18 20V10M12 20V4M6 20v-6"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </Svg>
);

const SupportIcon = ({ color, size = 24 }) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path
            d="M12 2C6.48 2 2 6.48 2 12v8c0 1.1.9 2 2 2h2v-9H4v-1c0-4.41 3.59-8 8-8s8 3.59 8 8v1h-2v9h2c1.1 0 2-.9 2-2v-8c0-5.52-4.48-10-10-10z"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
        />
        <Path
            d="M9 14v-2M15 14v-2"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
        />
    </Svg>
);

// Ícone de Troféu para Desafios
const TrophyIcon = ({ color, size = 24 }) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path
            d="M6 9H4.5C3.67 9 3 8.33 3 7.5V4.5C3 3.67 3.67 3 4.5 3H6"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <Path
            d="M18 9h1.5c.83 0 1.5-.67 1.5-1.5V4.5c0-.83-.67-1.5-1.5-1.5H18"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <Path
            d="M6 3h12v6c0 3.31-2.69 6-6 6s-6-2.69-6-6V3z"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <Path
            d="M12 15v3M8 21h8M10 18h4"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </Svg>
);

// Ícone de Livro Aberto para Questões
const BookOpenIcon = ({ color, size = 24 }) => (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <Path
            d="M2 3h6c1.1 0 2.1.45 2.83 1.17L12 5.34l1.17-1.17C13.9 3.45 14.9 3 16 3h6"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <Path
            d="M2 3v15c0 1.1.9 2 2 2h5.17c.53 0 1.04.21 1.42.59L12 22l1.41-1.41c.38-.38.89-.59 1.42-.59H20c1.1 0 2-.9 2-2V3"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <Path
            d="M12 5v17"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </Svg>
);

const TAB_CONFIG = [
    { key: 'Perfil', label: 'Perfil', icon: HomeIcon, route: 'Profile' },
    { key: 'Desempenho', label: 'Desempenho', icon: ChartIcon, route: 'Performance' },
    { key: 'Questões', label: 'Questões', icon: BookOpenIcon, route: 'Concursos' },
    { key: 'Desafios', label: 'Desafios', icon: TrophyIcon, route: 'Challenges' },
    { key: 'Suporte', label: 'Suporte', icon: SupportIcon, route: 'Support', isQuickView: true },
];

const BottomTabBar = memo(({ navigation, currentRoute = '', onSupportPress, onLayout }) => {
    const insets = useSafeAreaInsets();
    const { isDarkMode } = useTheme();
    const { isAuthenticated } = useAuth();
    const { unreadCount, hasUnreadResponse } = useSupportNotification();

    // Memoização das cores (evita recriação a cada render)
    const colors = useMemo(() => ({
        background: isDarkMode ? '#151821' : '#FFFFFF',
        activeColor: '#00BFA6', // Verde-água/turquesa
        inactiveColor: isDarkMode ? '#6B7280' : '#9CA3AF',
        border: isDarkMode ? '#2A2D3A' : '#E5E7EB',
    }), [isDarkMode]);

    // Memoização do handler (evita recriação a cada render)
    const handleTabPress = useCallback((tab) => {
        // WEB: o botao pressionado conserva foco enquanto o React Navigation
        // esconde a rota anterior com aria-hidden. Isso produz o warning
        // "descendant retained focus" e deixa o foco do teclado preso numa
        // arvore invisivel. Soltar antes da troca preserva a arvore acessivel.
        if (Platform.OS === 'web' && typeof document !== 'undefined') {
            document.activeElement?.blur?.();
        }

        // Se é QuickView (Suporte), chama callback especial
        if (tab.isQuickView && onSupportPress) {
            onSupportPress();
            return;
        }
        // Se clicou em Perfil e não está autenticado, vai para Login
        if (tab.key === 'Perfil' && !isAuthenticated) {
            navigation.navigate('UserAuth', { returnTo: 'Profile' });
            return;
        }
        // Navegação normal
        if (tab.route !== currentRoute) {
            navigation.navigate(tab.route);
        }
    }, [currentRoute, navigation, onSupportPress, isAuthenticated]);

    // Estilos dinâmicos memoizados
    const containerStyle = useMemo(() => [
        styles.container,
        {
            backgroundColor: colors.background,
            borderTopColor: colors.border,
            paddingBottom: insets.bottom > 0 ? insets.bottom : 6,
            paddingTop: PADDING_TOP,
        }
    ], [colors.background, colors.border, insets.bottom]);

    return (
        <View style={containerStyle} onLayout={onLayout}>
            {TAB_CONFIG.map((tab) => {
                const isActive = currentRoute === tab.route;
                const IconComponent = tab.icon;
                const iconColor = isActive ? colors.activeColor : colors.inactiveColor;

                return (
                    <Pressable
                        key={tab.key}
                        style={({ pressed }) => [
                            styles.tabButton,
                            pressed && styles.tabButtonPressed
                        ]}
                        onPress={() => handleTabPress(tab)}
                    >
                        <View style={styles.iconContainer}>
                            <IconComponent
                                color={iconColor}
                                size={ICON_SIZE}
                                filled={isActive && tab.key === 'Perfil'}
                            />
                            {/* Badge de notificação para Suporte */}
                            {tab.key === 'Suporte' && hasUnreadResponse && (
                                <View style={styles.badge}>
                                    <Text style={styles.badgeText}>
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </Text>
                                </View>
                            )}
                        </View>
                        <Text style={[
                            styles.tabLabel,
                            { color: iconColor }
                        ]}>
                            {tab.label}
                        </Text>
                    </Pressable>
                );
            })}
        </View>
    );
});

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        alignItems: 'center',
        minHeight: 56, // Altura mínima garantida
        borderTopWidth: 1,
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        // Efeito 3D discreto
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -3 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 10,
    },
    tabButton: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 2,
        minWidth: MIN_TAB_WIDTH,
        flex: 1, // Distribui igualmente
    },
    tabButtonPressed: {
        transform: [{ scale: 0.95 }],
        opacity: 0.8,
    },
    tabLabel: {
        fontSize: FONT_SIZE,
        fontWeight: '500',
        marginTop: MARGIN_TOP_LABEL,
    },
    iconContainer: {
        position: 'relative',
    },
    badge: {
        position: 'absolute',
        top: -4,
        right: -8,
        minWidth: 16,
        height: 16,
        borderRadius: 8,
        backgroundColor: '#EF4444',
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 4,
    },
    badgeText: {
        color: '#FFFFFF',
        fontSize: 10,
        fontWeight: '700',
    },
});

export default BottomTabBar;
